import { Link } from "react-router-dom";
import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ProjectCard } from "@/components/ProjectCard";
import { SectionHeading } from "@/components/SectionHeading";
import { ChapterArt } from "@/components/ChapterArt";
import {
  siteMeta,
  homepage,
  projects,
  capabilities,
  dimensions,
  contact,
} from "@/content/siteContent";

const featured = projects.filter((p) => p.featured);

const HEADLINE_LINES = ["I help turn complex", "business questions into", "clear strategic decisions."];

export default function Home() {
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const springX = useSpring(tiltX, { stiffness: 120, damping: 18, mass: 0.4 });
  const springY = useSpring(tiltY, { stiffness: 120, damping: 18, mass: 0.4 });

  const handlePointerMove = (e) => {
    if (reduce || e.pointerType !== "mouse" || !heroRef.current) return;
    const r = heroRef.current.getBoundingClientRect();
    tiltY.set(((e.clientX - r.left) / r.width - 0.5) * 5);
    tiltX.set(-((e.clientY - r.top) / r.height - 0.5) * 5);
  };
  const handlePointerLeave = () => {
    tiltX.set(0);
    tiltY.set(0);
  };

  return (
    <div data-testid="home-page">
      <section
        ref={heroRef}
        className="relative overflow-hidden bg-sand"
        data-testid="hero-section"
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      >
        <div className="relative mx-auto max-w-6xl px-6 pb-24 pt-20 sm:pb-28 sm:pt-28">
          <div className="relative grid lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:gap-16">
            <div>
          <Reveal>
            <p className="eyebrow" data-testid="hero-eyebrow">
              {homepage.heroEyebrow}
            </p>
          </Reveal>
          <h1
            className="mt-8 max-w-4xl text-[clamp(1.5rem,7.5vw,1.75rem)] font-black leading-[1.1] tracking-tighter sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            {HEADLINE_LINES.map((line, i) => (
              <span key={line} className="-mb-[0.12em] block overflow-hidden pb-[0.12em]">
                {reduce ? (
                  line
                ) : (
                  <motion.span
                    className="block"
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                )}
              </span>
            ))}
          </h1>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-muted sm:text-lg" data-testid="hero-intro">
              {homepage.heroIntro}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-4">
              <Link
                to="/work"
                className="editorial-link inline-flex items-center gap-2 text-base font-semibold"
                data-testid="hero-cta-work"
              >
                View selected work <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <Link
                to="/contact"
                className="editorial-link inline-flex items-center gap-2 text-base font-semibold"
                data-testid="hero-cta-contact"
              >
                Get in touch <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
            </div>
            <motion.div
              style={
                reduce
                  ? undefined
                  : { y: portraitY, rotateX: springX, rotateY: springY, transformPerspective: 900 }
              }
              className="-mb-24 sm:-mb-28 lg:self-end"
            >
            <Reveal delay={0.25} className="mt-16 lg:mt-0">
              <div className="relative mx-auto w-full max-w-64 sm:w-[22rem] sm:max-w-none lg:w-[30rem] xl:w-[33rem]" data-testid="hero-portrait">
                <svg
                  aria-hidden="true"
                  className="pointer-events-none absolute -inset-16 h-[calc(100%+8rem)] w-[calc(100%+8rem)]"
                  viewBox="0 0 600 800"
                  preserveAspectRatio="xMidYMid slice"
                  data-testid="hero-dotted-squares"
                >
                  <rect x="30" y="90" width="280" height="280" fill="none" stroke="#1A1A1A" strokeOpacity="0.16" strokeDasharray="3 6" vectorEffect="non-scaling-stroke" />
                  <rect x="330" y="380" width="230" height="230" fill="none" stroke="#7A8475" strokeOpacity="0.3" strokeDasharray="3 6" vectorEffect="non-scaling-stroke" />
                  <rect x="70" y="560" width="150" height="150" fill="none" stroke="#1A1A1A" strokeOpacity="0.12" strokeDasharray="3 6" vectorEffect="non-scaling-stroke" />
                </svg>
                <img
                  src="/images/epsa-portrait.png"
                  alt="Portrait of Epsa Kaithwar"
                  className="relative w-full"
                  data-testid="hero-portrait-image"
                />
              </div>
            </Reveal>
            </motion.div>
          </div>
        </div>
      </section>

      <div
        className="overflow-hidden border-y border-ink/10 bg-sand py-5"
        data-testid="capability-marquee"
        aria-hidden="true"
      >
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex shrink-0 items-center">
              {capabilities.map((c) => (
                <span key={`${dup}-${c.title}`} className="flex items-center">
                  <span className="px-6 text-xs font-medium uppercase tracking-[0.25em] text-muted">
                    {c.title}
                  </span>
                  <span className="h-1.5 w-1.5 rounded-full bg-terracotta" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <section className="bg-white" data-testid="about-section">
        <div className="relative mx-auto max-w-6xl px-6 py-24 sm:py-32">
          <ChapterArt testId="about-cluster-art" />
          <Reveal>
            <SectionHeading index="01" label="About" shortenRule />
          </Reveal>
          <div className="mt-14 grid gap-14 md:grid-cols-[1fr_1.2fr] md:gap-20">
            <Reveal delay={0.05}>
              <p className="text-xl font-semibold leading-snug tracking-tight sm:text-2xl">
                {siteMeta.positioning}
              </p>
              <p className="mt-6 text-sm leading-relaxed text-muted sm:text-base">
                {homepage.aboutSummary}
              </p>
              <Link
                to="/about"
                className="editorial-link mt-8 inline-flex items-center gap-2 text-base font-semibold"
                data-testid="about-summary-link"
              >
                More about me <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </Reveal>
            <div>
              {dimensions.map((d, i) => (
                <Reveal key={d.title} delay={0.1 + i * 0.08}>
                  {i > 0 && (
                    <div
                      className="my-11 h-px w-14 bg-ink/25"
                      aria-hidden="true"
                      data-testid="dimension-divider"
                    />
                  )}
                  <div>
                    <h3 className="text-base font-bold tracking-tight sm:text-lg">{d.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted sm:text-base">{d.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-terracotta" data-testid="selected-work-section">
        <div className="relative mx-auto max-w-6xl px-6 py-24 sm:py-32">
          <ChapterArt light testId="chapter-art-selected-work" />
          <Reveal>
            <SectionHeading terra index="02" label="Selected work" title="Case studies & featured projects" shortenRule />
          </Reveal>
          <div className="mt-16 grid gap-x-12 gap-y-16 md:grid-cols-2">
            {featured.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <ProjectCard project={p} terra />
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/work"
              className="editorial-link-on-dark mt-14 inline-flex items-center gap-2 text-base font-semibold"
              data-testid="selected-work-view-all"
            >
              View all work <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-charcoal" data-testid="capabilities-summary-section">
        <div className="relative mx-auto max-w-6xl px-6 py-24 sm:py-32">
          <ChapterArt light testId="chapter-art-capabilities" />
          <Reveal>
            <SectionHeading dark index="03" label="Capabilities & AI workflows" title="What I do" shortenRule />
          </Reveal>
          <div className="mt-6">
            {capabilities.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.05}>
                <div
                  className="grid gap-2 border-b border-sand/15 py-7 sm:grid-cols-[72px_240px_1fr] sm:gap-8"
                  data-testid={`capability-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="text-sm font-bold tracking-widest text-[#C96A63]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-base font-bold tracking-tight text-sand sm:text-lg">{c.title}</h3>
                  <p className="text-sm leading-relaxed text-[#C9CDC0] sm:text-base">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/capabilities"
              className="editorial-link-on-dark mt-10 inline-flex items-center gap-2 text-base font-semibold"
              data-testid="capabilities-summary-link"
            >
              Capabilities in full <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-white" data-testid="contact-cta-section">
        <div className="relative mx-auto max-w-6xl px-6 py-24 sm:py-32">
          <ChapterArt testId="chapter-art-contact" />
          <Reveal>
            <SectionHeading index="04" label="Contact" title="Working on a complex business question?" shortenRule />
            <div className="mt-12 flex flex-col gap-4">
              <a
                href={`mailto:${contact.email}`}
                className="editorial-link w-fit text-xl font-semibold tracking-tight sm:text-2xl"
                data-testid="home-contact-email-link"
              >
                {contact.email}
              </a>
              <a
                href={contact.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="editorial-link w-fit text-base font-medium text-muted"
                data-testid="home-contact-linkedin-link"
              >
                Connect on LinkedIn
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
